import * as React from "react";
import * as AppPath from "./model/AppPath";
import * as Redux from "redux";
import * as _ from "lodash";
import { connect } from "react-redux";
import { Route, Redirect, Switch, useParams, useLocation } from "react-router-dom";
import { ThunkDispatch } from "redux-thunk";
import * as FetchActions from "./actions/FetchActions";
import * as ProcessListActions from "./actions/ProcessListActions";

import useUserTheme from "shared/styles/hooks/useUserTheme";
import { ThemeProvider } from "styled-components";
import { DispatchType } from "../shared/utilities";
import { default as ProcessListContainer, ProcessInstanceListContainer } from "./components/ProcessListContainer";
import { ListState } from "./model/ListState";
import { State } from "./model/State";
import useDefaultAgentGroupInEnvironment from "shared/hooks/useDefaultAgentGroupInEnvironment";
import { changeFilter } from "./actions/SearchActions";
import { setProcessListTitleFromState } from "./model/AppTitles";
import StylesheetProvider from "shared/providers/StylesheetProvider";

const App: React.FC<React.PropsWithChildren<AppProps>> = (props) => {

    const processListURLBase = `/:base?/:processType(process|subprocess)/List`;
    const params = useParams<AppPath.IProcessListURL>();
    const location = useLocation();
    const [theme] = useUserTheme();

    const [currentAgentEnvironment, setCurrentAgentEnvironment] = React.useState<AppPath.AgentGroupEnvironment>(AppPath.getAgentGroupEnvironmentFromPath())

    const [currentSearch, setCurrentSearch] = React.useState<AppPath.ProcessListSearchParams>(AppPath.getSearchParameters());

    const instancesFromURL = AppPath.getProcessListURL().instances;
    const isOldInstancesRoute = instancesFromURL !== undefined && instancesFromURL !== "instances";

    const agentEnvironmentKey = params.environmentName + params.agentGroupName

    const firstAgentGroupInEnv = useDefaultAgentGroupInEnvironment({ agentGroups: Object.values(props.state.agentGroups), environmentName: params.environmentName });

    React.useEffect(() => {
        const runInitialActions = async () => {
            if (props.dispatch) {
                try {
                    // Currently the UI does full page refresh when chaning agent group so this is never run
                    if (!_.isEqual(currentAgentEnvironment, AppPath.getAgentGroupEnvironmentFromPath())) {
                        props.dispatch(ProcessListActions.changeAgentGroup(params.agentGroupName) as any);
                    } else {
                        const { processGuids } = AppPath.getSearchParameters()
                        await props.dispatch(FetchActions.fetchInitialData(AppPath.getAgentGroupEnvironmentFromPath(), processGuids.length == 0) as any);
                    }
                    setCurrentAgentEnvironment(AppPath.getAgentGroupEnvironmentFromPath())
                } catch (e) {
                    props.dispatch(FetchActions.fetchFinish(FetchActions.FetchId.all));
                    console.error(e);
                }
            }
        }
        runInitialActions();
    }, [params.processType, agentEnvironmentKey]);

    React.useEffect(() => {
        if (!instancesFromURL && props.state.agentGroups) {
            setProcessListTitleFromState(props.state);
        }
    }, [params.processType, instancesFromURL, props.state.currentAgentGroupName, props.state.agentGroups]);

    // Watch search query parameter changes and fetch new processes if needed 
    // Deselect all processes if anything changes
    React.useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const processListSearchParams = AppPath.getSearchParameters();

        if (props.dispatch) {
            const processGuids = processListSearchParams.processGuids;
            if (!_.isEmpty(processGuids)) {
                const existingProcessGuids = props.state.processes.map((p) => p.uniqueIdentifier);
                if (!processGuids.every((p) => existingProcessGuids.includes(p))) {
                    props.dispatch(FetchActions.fetchIndividualProcesses(params.environmentName, params.agentGroupName, processGuids) as any);
                }
                // Do not fetch new processes if we show instance list
                return;
            }

            if (!_.isEqual(processListSearchParams, currentSearch)) {
                props.dispatch(changeFilter(processListSearchParams.filterText as string, processListSearchParams.filterCategory as string) as any);
                props.dispatch(FetchActions.fetchProcesses() as any);
                setCurrentSearch(processListSearchParams);
            }
        }
    }, [location.search])

    return (
        <ThemeProvider theme={{ mode: theme }}>
            <StylesheetProvider>
                <Switch>
                    <Redirect exact={true} from={processListURLBase} to={`${processListURLBase}/Default/Default`} />
                    {isOldInstancesRoute && <Redirect to={`${processListURLBase}/:environmentName/:agentGroupName/instances?processGuids=${instancesFromURL}`} />}
                    {firstAgentGroupInEnv && <Redirect exact={true} from={`${processListURLBase}/:environmentName`} to={`${processListURLBase}/:environmentName/${firstAgentGroupInEnv}`} />}
                    <Route exact={true} path={`${processListURLBase}/:environmentName/:agentGroupName`}>
                        <ProcessListContainer key={agentEnvironmentKey} />
                    </Route>
                    <Route exact={true} path={`${processListURLBase}/:environmentName/:agentGroupName/instances`}>
                        <ProcessInstanceListContainer />
                    </Route>
                </Switch>
            </StylesheetProvider>
        </ThemeProvider>
    );

}

type AppProps = AppOwnProps & AppStateProps & AppDispatchProps;

interface AppOwnProps {
    initialSearchState?: Partial<ListState>;
    initialAgentGroupEnvironment?: AppPath.AgentGroupEnvironment;
}
interface AppStateProps {
    state: State;
}

interface AppDispatchProps {
    dispatch?: ThunkDispatch<State, void, Redux.Action>;
}

const mapStateToProps = (state: State): AppStateProps => {
    return {
        state
    }
}

const mapDispatchToProps = (dispatch: DispatchType<State>): AppDispatchProps => {
    return {
        dispatch: dispatch
    };
};

export default (connect(mapStateToProps, mapDispatchToProps)(App));
